import { lazy, Suspense } from 'react';

import { Spinner } from '@/common/components';
import { AppControllerType, AppRoutes } from '@/common/interfaces';
import { DynamicBaseBreadcrumb, DynamicDomainsBreadcrumb, DynamicSitesBreadcrumb } from '@/modules/AppBreadcrumb';

import { RedirectsController } from '@/common/controllers/Sites/redirects.controller';
import { ExternalRedirectsController } from './externalRedirects.controller';
import { ReferralsController } from './referrals.controller';

const SitesListPage = lazy(() => import('../../../pages/Sites/SitesList.page'));
const SitesOverviewPage = lazy(() => import('../../../pages/Sites/SitesOverview.page'));
const SiteDomainsDossierPage = lazy(() => import('../../../pages/Domains/SiteDomains/SiteDomainsDossier.page'));
const SiteDomainsListPage = lazy(() => import('../../../pages/Domains/SiteDomains/SiteDomainsList.page'));
const SitesCreatePage = lazy(() => import('../../../pages/Sites/SitesCreate.page'));
const SitesEditPage = lazy(() => import('../../../pages/Sites/SitesEdit.page'));
const SitesFSManagerPage = lazy(() => import('../../../pages/Sites/SitesFSManager.page'));
const SitesDossierPage = lazy(() => import('../../../pages/Sites/SitesDossier.page'));
const SitesTemplatesOverviewPage = lazy(() => import('../../../pages/Sites/SitesTemplates/SitesTemplatesOverview.page'));
const SitesCriticalCssPage = lazy(() => import('../../../pages/Sites/SitesCriticalCss.page'));
const RobotsDomainsListPage = lazy(() => import('../../../pages/Domains/Robots/RobotsDomainsList.page'));
const RobotsDossierPage = lazy(() => import('../../../pages/Domains/Robots/RobotsDossier.page'));
const SitemapDomainsListPage = lazy(() => import('../../../pages/Domains/Sitemap/SitemapDomainsList.page'));
const SitemapDossierPage = lazy(() => import('../../../pages/Domains/Sitemap/SitemapDossier.page'));

export const SitesController: AppControllerType = {
  common: [
    {
      path: AppRoutes.Sites + '/*',
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SitesListPage/>
        </Suspense>
      ),
      breadcrumb: DynamicSitesBreadcrumb.DynamicSitesListBreadcrumb
    },
    {
      path: AppRoutes.SitesById + '/*',
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SitesOverviewPage/>
        </Suspense>
      ),
      breadcrumb: DynamicSitesBreadcrumb.DynamicSitesByIdBreadcrumb
    },
    {
      path: AppRoutes.SiteDomains + '/*',
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SiteDomainsListPage/>
        </Suspense>
      ),
      breadcrumb: DynamicSitesBreadcrumb.DynamicSitesDomainsListBreadcrumb
    },
    {
      path: AppRoutes.SitesTemplates + '/*',
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SitesTemplatesOverviewPage/>
        </Suspense>
      ),
      breadcrumb: DynamicSitesBreadcrumb.DynamicSitesTemplatesBreadcrumb
    },
    {
      path: AppRoutes.SitesCriticalCss + '/*',
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SitesCriticalCssPage/>
        </Suspense>
      ),
      breadcrumb: DynamicSitesBreadcrumb.DynamicSitesCriticalCssBreadcrumb
    },
    {
      path: AppRoutes.Robots + '/*',
      element: (
        <Suspense fallback={ <Spinner/> }>
          <RobotsDomainsListPage/>
        </Suspense>
      ),
      breadcrumb: DynamicSitesBreadcrumb.DynamicRobotsBreadcrumb
    },
    {
      path: AppRoutes.Sitemap + '/*',
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SitemapDomainsListPage/>
        </Suspense>
      ),
      breadcrumb: DynamicSitesBreadcrumb.DynamicSitemapBreadcrumb
    },
    ...ReferralsController.common,
    ...RedirectsController.common,
    ...ExternalRedirectsController.common
  ],
  drawer: [
    {
      path: AppRoutes.SitesCreate,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SitesCreatePage/>
        </Suspense>
      ),
      breadcrumb: DynamicBaseBreadcrumb.DynamicBaseCreateBreadcrumb,
      props: {
        commonDrawerWidth: 400
      }
    },
    {
      path: AppRoutes.SitesEdit,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SitesEditPage/>
        </Suspense>
      ),
      breadcrumb: DynamicBaseBreadcrumb.DynamicBaseEditBreadcrumb,
      props: {
        commonDrawerWidth: 400
      }
    },
    {
      path: AppRoutes.SitesFSManager,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SitesFSManagerPage/>
        </Suspense>
      ),
      breadcrumb: DynamicSitesBreadcrumb.DynamicSitesFsManagerBreadcrumb,
      props: {
        commonDrawerWidth: 500
      }
    },
    {
      path: AppRoutes.SitesDossier,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SitesDossierPage/>
        </Suspense>
      ),
      breadcrumb: DynamicBaseBreadcrumb.DynamicBaseDossierBreadcrumb,
      props: {
        commonDrawerWidth: 400
      }
    },
    {
      path: AppRoutes.SiteDomainsById,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SiteDomainsDossierPage/>
        </Suspense>
      ),
      breadcrumb: DynamicDomainsBreadcrumb.DynamicDomainsByIdBreadcrumb,
      props: {
        commonDrawerWidth: 460
      }
    },
    {
      path: AppRoutes.RobotsById,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <RobotsDossierPage/>
        </Suspense>
      ),
      breadcrumb: DynamicDomainsBreadcrumb.DynamicDomainsByIdBreadcrumb,
      props: {
        commonDrawerWidth: 460
      }
    },
    {
      path: AppRoutes.SitemapById,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SitemapDossierPage/>
        </Suspense>
      ),
      breadcrumb: DynamicDomainsBreadcrumb.DynamicDomainsByIdBreadcrumb,
      props: {
        commonDrawerWidth: 600
      }
    },
    ...ReferralsController.drawer,
    ...RedirectsController.drawer,
    ...ExternalRedirectsController.drawer
  ]
};
